'use client';
import { isImage, resizeImageToMaxDimension } from '#pplx/frontend/common/files/images.js';
import { cleanCloudinaryUrl, isCloudinaryURL } from '#pplx/frontend/common/files/urls.js';
import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
import { batchCreateUploadUrls, subscribeAttachmentsBatch, } from '#pplx/frontend/components/hooks/attachments.js';
import { fileExtension, useTrackedFileUploadAnalytics, } from '#pplx/frontend/components/hooks/fileUploadAnalytics.js';
import { AttachmentProcessingErrorCodes } from '#pplx/frontend/components/utils/attachmentsUtils.js';
import { BLOCKED_UPLOAD_MIME_TYPES, BYTES_PER_MB, FALLBACK_MAX_FILE_UPLOAD_SIZE_MB, MIN_FILE_UPLOAD_SIZE, } from '#pplx/frontend/components/utils/consts.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
const MESSAGES = defineMessages({
    unsupportedType: {
        defaultMessage: 'File type {type} is not supported.',
        id: '71ef16a285',
        description: 'Error when uploading a blocked file type',
    },
    tooLarge: {
        defaultMessage: 'File is too large. Maximum size is {max}MB.',
        id: '0ba10babd2',
        description: 'Error when file exceeds the size limit',
    },
    tooSmall: {
        defaultMessage: 'File is too small.',
        id: '0611ccbe6a',
        description: 'Error when file is below minimum size',
    },
    noName: {
        defaultMessage: 'File has no name.',
        id: '67cc37beba',
        description: 'Error when file has no name',
    },
    uploadUrlsFailed: {
        defaultMessage: "We couldn't upload your file. Please try again.",
        id: '39d8b7dcbc',
        description: 'Error when presigned URL request fails',
    },
    uploadFailed: {
        defaultMessage: 'Upload failed.',
        id: 'f1866d5176',
        description: 'Generic upload failure error',
    },
    moderationFailed: {
        defaultMessage: 'File failed moderation.',
        id: 'f78b7fc20a',
        description: 'Error when file is rejected by moderation',
    },
    parsingFailed: {
        defaultMessage: 'File parsing failed.',
        id: '21c821c661',
        description: 'Error when backend file parsing fails',
    },
    parsingSubscriptionFailed: {
        defaultMessage: 'Parsing subscription failed.',
        id: 'c726d1b6b8',
        description: 'Error when SSE subscription for file parsing fails',
    },
    rateLimited: {
        defaultMessage: 'Upload limit reached. Please try again later.',
        id: 'b84056f299',
        description: 'Error when user exceeds daily upload rate limit',
    },
});
class ModerationRejectedError extends Error {
    constructor() {
        super('File failed moderation');
        this.name = 'ModerationRejectedError';
    }
}
export function terminalAfterStorageUpload(needsParsing) {
    return needsParsing ? null : { outcome: 'success' };
}
/**
 * @returns The file's terminal processing outcome, independent of the batch stream's final flag.
 */
export function terminalFromAttachmentProcessing(data) {
    // Each matching message is a file result; the final flag belongs to the batch stream.
    if (data.success ||
        data.token_limit_exceeded === true ||
        data.error_code === AttachmentProcessingErrorCodes.token_limit_exceeded) {
        return { outcome: 'success' };
    }
    switch (data.error_code) {
        case AttachmentProcessingErrorCodes.image_failed_moderation:
            return { outcome: 'failure', errorCode: 'moderation_rejected' };
        case AttachmentProcessingErrorCodes.failed_security_check:
            return { outcome: 'failure', errorCode: 'security_scan_failed' };
        case AttachmentProcessingErrorCodes.no_data_received:
            return { outcome: 'failure', errorCode: 'no_data_received' };
        case AttachmentProcessingErrorCodes.parsing_error:
        case AttachmentProcessingErrorCodes.file_type_not_supported:
            return { outcome: 'failure', errorCode: 'parse_error' };
        case AttachmentProcessingErrorCodes.unauthorized:
            return { outcome: 'failure', errorCode: 'no_permission' };
        case AttachmentProcessingErrorCodes.unknown_error:
        case null:
            return { outcome: 'failure', errorCode: 'index_failed' };
        default:
            return { outcome: 'failure', errorCode: 'index_failed' };
    }
}
function preserveRequestHeaders(headers) {
    return headers;
}
// -- Pure helpers (no React dependencies) --
export function validateFile({ file, maxFileSizeMB = FALLBACK_MAX_FILE_UPLOAD_SIZE_MB, skipParsing, }) {
    if (!skipParsing && BLOCKED_UPLOAD_MIME_TYPES.includes(file.type)) {
        return { code: 'unsupportedType', type: file.type };
    }
    if (file.size > maxFileSizeMB * BYTES_PER_MB) {
        return { code: 'tooLarge', max: maxFileSizeMB };
    }
    if (file.size < MIN_FILE_UPLOAD_SIZE) {
        return { code: 'tooSmall' };
    }
    if (!file.name) {
        return { code: 'noName' };
    }
    return null;
}
async function uploadToS3(fields, bucketUrl, file) {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
    });
    formData.append('file', file);
    const response = await fetch(bucketUrl, { method: 'POST', body: formData });
    if (!response.ok) {
        throw new Error(`S3 upload failed with status ${response.status}`);
    }
    // Cloudinary returns JSON with the uploaded URL; S3 returns no JSON body due to CORS.
    let data = null;
    try {
        data = await response.json();
    }
    catch (err) {
        logger.debug('[useUploadFiles] Upload response is not JSON (expected for S3)', {
            bucketUrl,
            err,
        });
    }
    if (data) {
        if (data.moderation?.[0]?.status === 'rejected') {
            throw new ModerationRejectedError();
        }
        const cloudinaryUrl = data.eager?.[0]?.secure_url ?? data.secure_url;
        if (cloudinaryUrl)
            return cleanCloudinaryUrl(cloudinaryUrl);
    }
    // Path-style S3 POST endpoints may omit the trailing directory separator.
    return `${bucketUrl.replace(/\/$/, '')}/${fields.key}`.replace('${filename}', file.name);
}
// -- Hook --
export function useUploadFiles({ reason, maxFileSizeMB = FALLBACK_MAX_FILE_UPLOAD_SIZE_MB, skipParsing = false, defaultFileSource, uploadRateLimit, onRateLimitUpdate, buildRequestHeaders = preserveRequestHeaders, analyticsSurface, }) {
    const { $t } = useIntl();
    const { started, completed, rejectInitiation } = useTrackedFileUploadAnalytics(analyticsSurface);
    const rejectFiles = useCallback((files, errorCode, selectionFileCount = files.length) => {
        rejectInitiation(files, 'local', errorCode, selectionFileCount);
    }, [rejectInitiation]);
    const translateError = useCallback((error) => {
        switch (error.code) {
            case 'unsupportedType':
                return $t(MESSAGES.unsupportedType, { type: error.type });
            case 'tooLarge':
                return $t(MESSAGES.tooLarge, { max: error.max });
            case 'tooSmall':
                return $t(MESSAGES.tooSmall);
            case 'noName':
                return $t(MESSAGES.noName);
        }
    }, [$t]);
    const resizeImages = useCallback(async ({ entries, onUpdate, }) => {
        if (defaultFileSource === 'asi')
            return;
        const images = entries.filter((e) => isImage(e.file) && !e.file.type.includes('svg'));
        if (images.length === 0)
            return;
        await Promise.all(images.map(async (entry) => {
            try {
                entry.file = await resizeImageToMaxDimension(entry.file);
            }
            catch {
                logger.warn('[useUploadFiles] Failed to resize image', {
                    fileNameLength: entry.fileName.length,
                });
                return;
            }
            // Consumers need the replacement File while storage is still pending.
            onUpdate(entry.id, { file: entry.file });
        }));
    }, [defaultFileSource]);
    const uploadBatchToS3 = useCallback(async (entries, batchResults, update, attempts, complete) => {
        await Promise.all(entries.map(async (entry) => {
            const res = batchResults[entry.id];
            if (!res || res.error || res.rate_limited || !res.fields || !res.s3_bucket_url) {
                const error = res?.rate_limited
                    ? $t(MESSAGES.rateLimited)
                    : (res?.error ?? $t(MESSAGES.uploadFailed));
                update(entry.id, { status: 'failed', error });
                complete(entry.id, 'failure', res?.rate_limited
                    ? 'rate_limited'
                    : res?.error === 'attachments_disabled_by_organization'
                        ? 'org_uploads_disabled'
                        : 'presign_failed');
                return;
            }
            try {
                const attempt = attempts.get(entry.id);
                if (attempt)
                    attempt.fileUUID = res.file_uuid ?? null;
                const url = await uploadToS3(res.fields, res.s3_bucket_url, entry.file);
                const needsParsing = !isCloudinaryURL(url) && !!res.file_uuid && !skipParsing;
                update(entry.id, {
                    url,
                    serverId: res.file_uuid ?? undefined,
                    status: needsParsing ? 'parsing' : 'success',
                });
                const terminal = terminalAfterStorageUpload(needsParsing);
                if (terminal)
                    complete(entry.id, terminal.outcome, terminal.errorCode);
            }
            catch (err) {
                update(entry.id, {
                    status: 'failed',
                    error: err instanceof ModerationRejectedError
                        ? $t(MESSAGES.moderationFailed)
                        : $t(MESSAGES.uploadFailed),
                });
                complete(entry.id, 'failure', err instanceof ModerationRejectedError ? 'moderation_rejected' : 'storage_rejected');
            }
        }));
    }, [$t, skipParsing]);
    const waitForParsing = useCallback(async (entries, update, complete) => {
        if (entries.length === 0)
            return;
        const byServerId = new Map(entries.map((e) => [e.serverId, e]));
        try {
            await subscribeAttachmentsBatch({
                request: { file_uuids: [...byServerId.keys()] },
                reason,
                headers: buildRequestHeaders({}),
                onMessage: (data) => {
                    const entry = byServerId.get(data.file_uuid);
                    if (!entry)
                        return;
                    const terminal = terminalFromAttachmentProcessing(data);
                    update(entry.id, terminal.outcome === 'success'
                        ? { status: 'success', url: data.s3_url ?? entry.url }
                        : {
                            status: 'failed',
                            error: $t(MESSAGES.parsingFailed),
                            ...(terminal.errorCode === undefined
                                ? {}
                                : { failureCode: terminal.errorCode }),
                        });
                    complete(entry.id, terminal.outcome, terminal.errorCode);
                },
            });
        }
        catch {
            // The unresolved attempts are settled below with the same transport failure.
        }
        for (const entry of entries) {
            if (entry.status === 'parsing') {
                update(entry.id, {
                    status: 'failed',
                    error: $t(MESSAGES.parsingSubscriptionFailed),
                    failureCode: 'client_no_response',
                });
                complete(entry.id, 'failure', 'client_no_response');
            }
        }
    }, [$t, reason, buildRequestHeaders]);
    const uploadFiles = useCallback(async (files, onChange, signal) => {
        const tracked = new Map();
        const attempts = new Map();
        const complete = (id, outcome, errorCode) => {
            const attempt = attempts.get(id);
            if (!attempt)
                return;
            attempts.delete(id);
            completed(attempt, outcome, errorCode);
        };
        signal?.addEventListener('abort', () => {
            attempts.forEach((_attempt, id) => complete(id, 'failure', 'client_no_response'));
        }, { once: true });
        const update = (id, patch) => {
            if (signal?.aborted)
                return;
            const entry = tracked.get(id);
            if (!entry)
                return;
            Object.assign(entry, patch);
            onChange?.([{ ...entry }]);
        };
        // 1. Validate
        for (const file of files) {
            const id = safeRandomUUID();
            const validationError = validateFile({ file, maxFileSizeMB, skipParsing });
            const attempt = {
                uploadAttemptUUID: id,
                fileUUID: null,
                sourceType: 'local',
                fileSizeBytes: file.size,
                fileExtension: fileExtension(file.name),
                fileCount: files.length,
            };
            attempts.set(id, attempt);
            started(attempt);
            tracked.set(id, {
                id,
                file,
                fileName: file.name,
                fileSize: file.size,
                fileType: file.type,
                status: validationError ? 'failed' : 'uploading',
                error: validationError ? translateError(validationError) : undefined,
            });
            if (validationError) {
                complete(id, 'failure', validationError.code === 'tooLarge' ? 'file_too_large' : 'unsupported_type');
            }
        }
        onChange?.([...tracked.values()].map((e) => ({ ...e })));
        const toUpload = [...tracked.values()].filter((e) => e.status === 'uploading');
        if (toUpload.length === 0)
            return;
        // 2. Check rate limit
        if (uploadRateLimit !== undefined && toUpload.length > uploadRateLimit) {
            for (const entry of toUpload) {
                update(entry.id, { status: 'failed', error: $t(MESSAGES.rateLimited) });
                complete(entry.id, 'failure', 'rate_limited');
            }
            return;
        }
        // 3. Resize images
        await resizeImages({ entries: toUpload, onUpdate: update });
        if (signal?.aborted)
            return;
        // 4. Get presigned URLs
        let batchResults;
        try {
            batchResults = await batchCreateUploadUrls({
                filesWithUuids: toUpload.map(({ id, file }) => ({ uuid: id, file })),
                fileSource: defaultFileSource,
                skipParsing,
                reason,
                headers: buildRequestHeaders({}),
            });
        }
        catch (error) {
            toUpload.forEach((entry) => complete(entry.id, 'failure', 'presign_failed'));
            throw error;
        }
        if (!batchResults) {
            for (const entry of toUpload) {
                update(entry.id, { status: 'failed', error: $t(MESSAGES.uploadUrlsFailed) });
                complete(entry.id, 'failure', 'presign_failed');
            }
            return;
        }
        if (signal?.aborted)
            return;
        // 5. Upload to S3
        await uploadBatchToS3(toUpload, batchResults, update, attempts, complete);
        // Count files that received presigned URLs — the backend debits the rate limit at that point
        const sentCount = toUpload.filter((e) => batchResults[e.id]).length;
        if (sentCount > 0) {
            onRateLimitUpdate?.(sentCount);
        }
        if (signal?.aborted)
            return;
        // 6. Wait for parsing
        const pendingParse = [...tracked.values()].filter((e) => e.status === 'parsing' && e.serverId);
        await waitForParsing(pendingParse, update, complete);
    }, [
        maxFileSizeMB,
        skipParsing,
        defaultFileSource,
        reason,
        $t,
        translateError,
        uploadRateLimit,
        onRateLimitUpdate,
        resizeImages,
        uploadBatchToS3,
        waitForParsing,
        buildRequestHeaders,
        completed,
        started,
    ]);
    return { rejectFiles, uploadFiles };
}
//# sourceMappingURL=useUploadFiles.js.map